import React from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Box,
  Menu,
  MenuItem,
  Avatar,
} from '@mui/material';
import { Link, useNavigate } from 'react-router-dom';
import { useAxios } from '../context/AxiosContext';

const Navbar = () => {
  const { isAuthenticated, user, logout, loading } = useAxios();
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = React.useState(null);

  const handleMenu = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    logout();
    handleClose();
    navigate('/');
  };

  const handleDashboard = () => {
    navigate('/dashboard');
    handleClose();
  };

  if (loading) {
    return null; // Don't show navbar while loading
  }

  return (
    <AppBar position="static" elevation={0}>
      <Toolbar sx={{ px: { xs: 2, md: 4 } }}>
        <Typography
          variant="h6"
          component={Link}
          to="/"
          sx={{
            flexGrow: 1,
            textDecoration: 'none',
            color: 'inherit',
            fontWeight: 800,
            fontSize: '1.5rem',
            background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
            backgroundClip: 'text',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            transition: 'all 0.2s ease-in-out',
            '&:hover': {
              transform: 'scale(1.05)',
            },
          }}
        >
          ✨ Wizcord
        </Typography>

        {isAuthenticated ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Button
              color="inherit"
              component={Link}
              to="/dashboard"
              sx={{ 
                textTransform: 'none',
                fontWeight: 600,
                border: '1px solid rgba(99, 102, 241, 0.3)',
                borderRadius: '8px',
                background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.1) 0%, rgba(139, 92, 246, 0.1) 100%)',
                '&:hover': { 
                  backgroundColor: 'rgba(99, 102, 241, 0.2)',
                  borderColor: 'rgba(99, 102, 241, 0.5)',
                  transform: 'translateY(-1px)',
                },
                transition: 'all 0.2s ease-in-out',
              }}
            >
              📊 Dashboard
            </Button>
            <Button
              color="inherit"
              component={Link}
              to="/server-admin"
              sx={{ 
                textTransform: 'none',
                fontWeight: 600,
                border: '1px solid rgba(139, 92, 246, 0.3)',
                borderRadius: '8px',
                background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.1) 0%, rgba(99, 102, 241, 0.1) 100%)',
                '&:hover': { 
                  backgroundColor: 'rgba(139, 92, 246, 0.2)',
                  borderColor: 'rgba(139, 92, 246, 0.5)',
                  transform: 'translateY(-1px)',
                },
                transition: 'all 0.2s ease-in-out',
              }}
            >
              ⚙️ Server Administration
            </Button>
            <Button
              color="inherit"
              component={Link}
              to="/examples"
              sx={{ 
                textTransform: 'none',
                fontWeight: 500,
                '&:hover': { 
                  backgroundColor: 'rgba(99, 102, 241, 0.1)',
                },
              }}
            >
              Examples
            </Button>
            <Button
              color="inherit"
              component={Link}
              to="/docs"
              sx={{ 
                textTransform: 'none',
                fontWeight: 500,
                '&:hover': { 
                  backgroundColor: 'rgba(99, 102, 241, 0.1)',
                },
              }}
            >
              Documentation
            </Button>
            <Button
              color="inherit"
              component={Link}
              to="/support"
              sx={{ 
                textTransform: 'none',
                fontWeight: 500,
                '&:hover': { 
                  backgroundColor: 'rgba(99, 102, 241, 0.1)',
                },
              }}
            >
              Support
            </Button>

            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                cursor: 'pointer',
                padding: '8px 16px',
                borderRadius: '12px',
                transition: 'all 0.2s ease-in-out',
                '&:hover': {
                  backgroundColor: 'rgba(99, 102, 241, 0.1)',
                },
              }}
              onClick={handleMenu}
            >
              <Avatar
                sx={{
                  width: 32,
                  height: 32,
                  bgcolor: 'primary.main',
                  fontSize: '0.875rem',
                  fontWeight: 600,
                }}
              >
                {user?.email?.charAt(0).toUpperCase() || 'U'}
              </Avatar>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                {user?.email?.split('@')[0] || 'User'}
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.7, fontSize: '0.75rem' }}>
                ▼
              </Typography>
            </Box>
            <Menu
              id="menu-appbar"
              anchorEl={anchorEl}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'right',
              }}
              keepMounted
              transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
              open={Boolean(anchorEl)}
              onClose={handleClose}
              sx={{
                mt: 1,
                '& .MuiPaper-root': {
                  minWidth: 180,
                  borderRadius: '12px',
                  backgroundColor: 'background.paper',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  boxShadow: '0 10px 40px rgba(0, 0, 0, 0.3)',
                },
              }}
            >
              <MenuItem 
                onClick={handleDashboard}
                sx={{ 
                  fontWeight: 600,
                  background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.05) 0%, rgba(139, 92, 246, 0.05) 100%)',
                  border: '1px solid rgba(99, 102, 241, 0.2)',
                  borderRadius: '6px',
                  margin: '2px 8px',
                  '&:hover': { 
                    backgroundColor: 'rgba(99, 102, 241, 0.15)',
                    borderColor: 'rgba(99, 102, 241, 0.4)'
                  }
                }}
              >
                📊 Dashboard
              </MenuItem>
              <MenuItem 
                onClick={() => { handleClose(); navigate('/server-admin'); }}
                sx={{ 
                  fontWeight: 600,
                  background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.05) 0%, rgba(99, 102, 241, 0.05) 100%)',
                  border: '1px solid rgba(139, 92, 246, 0.2)',
                  borderRadius: '6px',
                  margin: '2px 8px',
                  '&:hover': { 
                    backgroundColor: 'rgba(139, 92, 246, 0.15)',
                    borderColor: 'rgba(139, 92, 246, 0.4)'
                  }
                }}
              >
                ⚙️ Server Administration
              </MenuItem>
              {user?.is_superuser && (
                <MenuItem 
                  onClick={() => { handleClose(); navigate('/jfdjfd-superuser-admin'); }}
                  sx={{ 
                    fontWeight: 600,
                    background: 'linear-gradient(135deg, rgba(220, 38, 127, 0.05) 0%, rgba(239, 68, 68, 0.05) 100%)',
                    border: '1px solid rgba(220, 38, 127, 0.2)',
                    borderRadius: '6px',
                    margin: '2px 8px',
                    '&:hover': { 
                      backgroundColor: 'rgba(220, 38, 127, 0.15)',
                      borderColor: 'rgba(220, 38, 127, 0.4)'
                    }
                  }}
                >
                  🔧 Admin Dashboard
                </MenuItem>
              )}
              <MenuItem 
                onClick={() => { handleClose(); navigate('/examples'); }}
                sx={{ 
                  fontWeight: 500,
                  '&:hover': { backgroundColor: 'rgba(99, 102, 241, 0.1)' }
                }}
              >
                🌟 Examples
              </MenuItem>
              <MenuItem 
                onClick={() => { handleClose(); navigate('/docs'); }}
                sx={{ 
                  fontWeight: 500,
                  '&:hover': { backgroundColor: 'rgba(99, 102, 241, 0.1)' }
                }}
              >
                📚 Documentation
              </MenuItem>
              <MenuItem 
                onClick={() => { handleClose(); navigate('/support'); }}
                sx={{ 
                  fontWeight: 500,
                  '&:hover': { backgroundColor: 'rgba(99, 102, 241, 0.1)' }
                }}
              >
                Support
              </MenuItem>
              <MenuItem 
                onClick={handleLogout}
                sx={{ 
                  fontWeight: 500,
                  color: 'error.main',
                  '&:hover': { backgroundColor: 'rgba(239, 68, 68, 0.1)' }
                }}
              >
                🚪 Logout
              </MenuItem>
            </Menu>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <Button
              color="inherit"
              component={Link}
              to="/examples"
              sx={{ 
                textTransform: 'none',
                fontWeight: 500,
                '&:hover': { 
                  backgroundColor: 'rgba(99, 102, 241, 0.1)',
                },
              }}
            >
              Examples
            </Button>
            <Button
              color="inherit"
              component={Link}
              to="/docs"
              sx={{ 
                textTransform: 'none',
                fontWeight: 500,
                '&:hover': { 
                  backgroundColor: 'rgba(99, 102, 241, 0.1)',
                },
              }}
            >
              Documentation
            </Button>
            <Button
              color="inherit"
              component={Link}
              to="/support"
              sx={{ 
                textTransform: 'none',
                fontWeight: 500,
                '&:hover': { 
                  backgroundColor: 'rgba(99, 102, 241, 0.1)',
                },
              }}
            >
              Support
            </Button>
            <Button
              color="inherit"
              component={Link}
              to="/pricing"
              sx={{ 
                textTransform: 'none',
                fontWeight: 500,
                '&:hover': { 
                  backgroundColor: 'rgba(99, 102, 241, 0.1)',
                },
              }}
            >
              Pricing
            </Button>
            <Button
              color="inherit"
              component={Link}
              to="/login"
              sx={{ 
                textTransform: 'none',
                fontWeight: 500,
                '&:hover': { 
                  backgroundColor: 'rgba(99, 102, 241, 0.1)',
                },
              }}
            >
              Sign In
            </Button>
            <Button
              component={Link}
              to="/register"
              variant="contained"
              sx={{ 
                textTransform: 'none',
                fontWeight: 600,
                background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)',
                },
              }}
            >
              Get Started
            </Button>
          </Box>
        )}
      </Toolbar>
    </AppBar>
  );
};

export default Navbar; 